import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  useTheme
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../../components/Dashboard/Header";
import { tokens } from "../../../theme";
import axios from "axios";
import { schemeCategory10 } from 'd3-scale-chromatic';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

// Shared TextField styles
const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.9rem",
    height: "45px",
    "& fieldset": { borderColor: "#771726" },
    "&:hover fieldset": { borderColor: "#771726" },
    "&.Mui-focused fieldset": { borderColor: "#771726" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.85rem",
    color: "#000000",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#000000",
  },
};

// Current datetime-helper
const nowLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtNoSeconds = (v) =>
  typeof v === "string" ? v.replace(/:\d{2}$/, "") : v;

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1, bgcolor: '#222', border: '1px solid #555' }}>
      <Typography variant="caption" sx={{ color: '#ddd' }}>
        {fmtNoSeconds(label)}
      </Typography>
      {payload.map((entry) => (
        <Typography key={entry.dataKey} variant="body2" sx={{ color: entry.color }}>
          {entry.name || entry.dataKey}: {entry.value}
        </Typography>
      ))}
    </Paper>
  );
}


// SERIES (para checkboxes/leyenda/gráfico)
const seriesOptions = [
  // Analógicas
  { key: "tempSuccionComp1",        label: "Temp. Succión Comp. 1" },
  { key: "tempProcesoEntrada",      label: "Temp. Proceso Entrada" },
  { key: "tempProcesoSalida",       label: "Temp. Proceso Salida" },
  { key: "tempTorreEntrada",        label: "Temp. Torre Entrada" },
  { key: "tempTorreSalida",         label: "Temp. Torre Salida" },
  { key: "tempDescargaComp1",       label: "Temp. Descarga Compresor 1" },
  { key: "tempCoThermEntrada",      label: "Temp. Co-Therm Entrada" },
  { key: "tempRefrPrecalSalidaC1",  label: "Temp. Refr Precal Salida C1" },
  { key: "tempCoThermSalida",       label: "Temp. Co-Therm Salida" },
  { key: "presionSuccionComp1",     label: "Presión Succión Comp. 1" },
  { key: "presionDescargaComp1",    label: "Presión Descarga Comp. 1" },
  { key: "voltajeCmp1",             label: "Voltaje Cmp 1" },
  { key: "amperajeCmp1",            label: "Amperaje Cmp 1" },
  { key: "sobrecalentamientoCmp1",  label: "Sobrecalentamiento Cmp 1" },
  { key: "condicionActualComp1",    label: "Condición Actual Comp. 1" },
  { key: "porcentajeCargaComp1",    label: "Porcentaje Carga Comp. 1 (%)" },

  // Digitales
  { key: "controlCarga",                label: "Control de Carga" },
  { key: "ramploading",                 label: "Ramp Loading" },
  { key: "switchFlujoAguaCondensacion", label: "Switch Flujo Agua Condensación" },
  { key: "switchFlujoAguaHelada",       label: "Switch Flujo Agua Helada" },
  { key: "bajoNivelAceiteComp1",        label: "Bajo Nivel Aceite Comp. 1" },
  { key: "statusTempMotorComp1",        label: "Status Temp Motor Comp. 1" },
  { key: "interlockComp1",              label: "Interlock Comp. 1" },
  { key: "statusMotorProtect1",         label: "Status MotorProtect 1" },
  { key: "arranqueRemoto",              label: "Arranque Remoto" },
  { key: "paroEmergencia",              label: "Paro de Emergencia" },
  { key: "encendidoComp1",              label: "Encendido Compresor 1" },
  { key: "loadUnloadValvula1Comp1",     label: "Load/Unload Válvula 1 Comp. 1" },
  { key: "loadUnloadValvula2Comp1",     label: "Load/Unload Válvula 2 Comp. 1" },
  { key: "loadUnloadValvula3Comp1",     label: "Load/Unload Válvula 3 Comp. 1" },
  { key: "output04",                    label: "Output 04" },
  { key: "output05",                    label: "Output 05" },
  { key: "output06",                    label: "Output 06" },
  { key: "output07",                    label: "Output 07" },
  { key: "output08",                    label: "Output 08" },
  { key: "output09",                    label: "Output 09" },
  { key: "output10",                    label: "Output 10" },
  { key: "encendidoCoThermC1",          label: "Encendido Co-Therm C1" },
  { key: "output12",                    label: "Output 12" },
  { key: "output13",                    label: "Output 13" },
];

const GLicuador1Comp = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Form state
  const [idEquipo, setIdEquipo] = useState("");
  const [fechaInicio, setFechaInicio] = useState(nowLocal());
  const [fechaFinal, setFechaFinal] = useState(nowLocal());

  // Tab state
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (_, newIndex) => setTabIndex(newIndex);

  // Data state
  const [rows, setRows] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE;
  const MACHINE  = "Optichiller 1Comp CT6";
  const QUERY    = "Grafica";


  // toggle selection
  const toggleKey = (k) => {
    setSelectedKeys((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
    );
  };

  const fetchRows = async () => {
  // 1) validación de fechas
  if (new Date(fechaFinal) < new Date(fechaInicio)) {
    setError("La fecha final no puede ser anterior a la fecha de inicio.");
    return;
  }

  setError(null);
  setLoading(true);
  setRows([]);

  try {
    // 2) petición
    const res = await axios.post(
  `${API_BASE}/test/consulta/` +
    `${encodeURIComponent(MACHINE)}/` +
    `${encodeURIComponent(QUERY)}`,
  { idEquipo, fechaInicio, fechaFinal },
  { headers: { "Content-Type": "application/json" } }
  );


    // 3) parsear body
    const body = res.data.body
      ? (typeof res.data.body === "string"
          ? JSON.parse(res.data.body)
          : res.data.body)
      : res.data;

    //console.log("👉 body ya parseado:", JSON.stringify(body, null, 2));

    // 4) extraer array
    const extractedData = Array.isArray(body.data)
      ? body.data
      : (Array.isArray(body.data?.[0]) ? body.data[0] : []);

    // 5) limpiar claves
    const normalizeKeys = row => {
      const clean = {};
      for (const k in row) clean[k.trim()] = row[k];
      return clean;
    };
    
    // 6) mapeo completo (copia aquí TODOS tus campos)
    const mapped = extractedData.map((row, i) => {
      const r = normalizeKeys(row);
      const fechaUTC = new Date(r["Fecha"]);
      const ts = fechaUTC.getTime();
      const formattedDate = 
        `${fechaUTC.getUTCFullYear()}-${String(fechaUTC.getUTCMonth()+1).padStart(2,"0")}`+
        `-${String(fechaUTC.getUTCDate()).padStart(2,"0")} `+
        `${String(fechaUTC.getUTCHours()).padStart(2,"0")}:`+
        `${String(fechaUTC.getUTCMinutes()).padStart(2,"0")}:`+
        `${String(fechaUTC.getUTCSeconds()).padStart(2,"0")}`;

        const base = {
          id: i + 1,
          ts,
          tipoMaquinaControl: row["Tipo de Máquina y Control"],
          numeroEquipo: row["Número de Equipo"],
          fecha: formattedDate, // ✅ Se usa la fecha convertida aquí
          tempSuccionComp1: row["Temp. Succion Comp 1"],
          tempProcesoEntrada: row["Temp. Proceso Entrada"],
          tempProcesoSalida: row["Temp. Proceso Salida"],
          tempTorreEntrada: row["Temp. Torre Entrada"],
          tempTorreSalida: row["Temp. Torre Salida"],
          tempDescargaComp1: row["Temp. Descarga Compresor 1"],
          tempCoThermEntrada: row["Temp. CoTherm Entrada"],
          tempRefrPrecalSalidaC1: row["Temp. Refr Precal Salida Circuito 1"],
          tempCoThermSalida: row["Temp. CoTherm Salida"],
          presionSuccionComp1: row["Presion Succion Comp 1"],
          presionDescargaComp1: row["Presion Descarga Comp 1"],
          voltajeCmp1: row["Voltaje Cmp 1"],
          amperajeCmp1: row["Amperaje Cmp 1"],
          sobrecalentamientoCmp1: row["Sobrecalentamiento Cmp 1"],
          condicionActualComp1: row["Condicion Actual Comp 1"],
          porcentajeCargaComp1: row["Porcentaje Carga Comp 1"],
          controlCarga: row["Control de Carga"],
          ramploading: row["Ramploading"],
          switchFlujoAguaCondensacion: row["Switch Flujo Agua Condensacion"],
          switchFlujoAguaHelada: row["Switch Flujo Agual Helada"],
          bajoNivelAceiteComp1: row["Bajo Nivel Aceite Comp. 1"],
          statusTempMotorComp1: row["Status Temp Motor Comp. 1"],
          interlockComp1: row["Interlock Comp. 1"],
          statusMotorProtect1: row["Status MotorProtect 1"],
          arranqueRemoto: row["Arranque Remoto"],
          paroEmergencia: row["Paro de Emergencia"],
          encendidoComp1: row["Encendido Compresor 1"],
          loadUnloadValvula1Comp1: row["Load/Unload Valvula 1 Comp. 1"],
          loadUnloadValvula2Comp1: row["Load/Unload Valvula 2 Comp. 1"],
          loadUnloadValvula3Comp1: row["Load/Unload Valvula 3 Comp. 1"],
          output04: row["Output_04"],
          output05: row["Output_05"],
          output06: row["Output_06"],
          output07: row["Output_07"],
          output08: row["Output_08"],
          output09: row["Output_09"],
          output10: row["Output_10"],
          encendidoCoThermC1: row["Encendido Co-Therm C1"],
          output12: row["Output_12"],
          output13: row["Output_13"],
          };

    // 🔌 OPCIONAL (pero recomendable si tus datos vienen como strings/ON/OFF):
     //Normaliza todo lo que no sea texto a número/0-1
     const parseValue = (v) => {
      if (v === null || v === undefined || v === "") return null;
      const s = String(v).trim().toLowerCase();
       if (["true","on","encendido","sí","si","1"].includes(s)) return 1;
       if (["false","off","apagado","no","0"].includes(s)) return 0;
       const n = Number(s.replace(",", "."));
       return Number.isFinite(n) ? n : v;
    };
     const TEXT_KEYS = new Set(["id","tipoMaquinaControl","numeroEquipo","fecha","ts"]);
     Object.keys(base).forEach(k => {
       if (!TEXT_KEYS.has(k)) base[k] = parseValue(base[k]);
     });

    return base;
      })

    // 6.1) ✅ Ordena una sola vez aquí
    mapped.sort((a, b) => {
      const ta = a.ts ?? Date.parse(a.fecha) ?? 0;
      const tb = b.ts ?? Date.parse(b.fecha) ?? 0;
      return ta - tb; // ascendente: del más viejo al más nuevo
    });

      setRows(mapped);

  } catch (err) {
    console.error(err);
    setError("Error al consultar los datos. Intenta de nuevo.");
  } finally {
    setLoading(false);
  }
};

  // keys que corresponden a series análogas y digitales
  const analogKeys = [
    "tempSuccionComp1", "tempProcesoEntrada", "tempProcesoSalida", "tempTorreEntrada",
    "tempTorreSalida", "tempDescargaComp1", "tempCoThermEntrada", "tempRefrPrecalSalidaC1",
    "tempCoThermSalida", "presionSuccionComp1", "presionDescargaComp1", "voltajeCmp1",
    "amperajeCmp1", "sobrecalentamientoCmp1", "condicionActualComp1", "porcentajeCargaComp1",
  ];

  const digitalKeys = [
    "controlCarga", "ramploading", "switchFlujoAguaCondensacion", "switchFlujoAguaHelada",
    "bajoNivelAceiteComp1", "statusTempMotorComp1", "interlockComp1", "statusMotorProtect1",
    "arranqueRemoto", "paroEmergencia", "encendidoComp1", "loadUnloadValvula1Comp1",
    "loadUnloadValvula2Comp1", "loadUnloadValvula3Comp1", "output04", "output05",
    "output06", "output07", "output08", "output09",
    "output10", "encendidoCoThermC1", "output12", "output13",
  ];

  // COLUMNAS (DataGrid)
  const columns = [
    { field: "id",                  headerName: "ID",                           width: 90 },

    // Meta
    { field: "tipoMaquinaControl",  headerName: "Tipo de Máquina y Control",    minWidth: 220, flex: 1 },
    { field: "numeroEquipo",        headerName: "Número de Equipo",             minWidth: 160, flex: 1 },
    { field: "fecha",               headerName: "Fecha",                        minWidth: 180, flex: 1 },

    // Analógicas
    { field: "tempSuccionComp1",        headerName: "Temp. Succión Comp. 1",           minWidth: 210, flex: 1 },
    { field: "tempProcesoEntrada",      headerName: "Temp. Proceso Entrada",           minWidth: 200, flex: 1 },
    { field: "tempProcesoSalida",       headerName: "Temp. Proceso Salida",            minWidth: 200, flex: 1 },
    { field: "tempTorreEntrada",        headerName: "Temp. Torre Entrada",             minWidth: 200, flex: 1 },
    { field: "tempTorreSalida",         headerName: "Temp. Torre Salida",              minWidth: 200, flex: 1 },
    { field: "tempDescargaComp1",       headerName: "Temp. Descarga Compresor 1",      minWidth: 230, flex: 1 },
    { field: "tempCoThermEntrada",      headerName: "Temp. Co-Therm Entrada",          minWidth: 220, flex: 1 },
    { field: "tempRefrPrecalSalidaC1",  headerName: "Temp. Refr Precal Salida C1",     minWidth: 240, flex: 1 },
    { field: "tempCoThermSalida",       headerName: "Temp. Co-Therm Salida",           minWidth: 220, flex: 1 },
    { field: "presionSuccionComp1",     headerName: "Presión Succión Comp. 1",         minWidth: 210, flex: 1 },
    { field: "presionDescargaComp1",    headerName: "Presión Descarga Comp. 1",        minWidth: 210, flex: 1 },
    { field: "voltajeCmp1",             headerName: "Voltaje Cmp 1",                   minWidth: 180, flex: 1 },
    { field: "amperajeCmp1",            headerName: "Amperaje Cmp 1",                  minWidth: 180, flex: 1 },
    { field: "sobrecalentamientoCmp1",  headerName: "Sobrecalentamiento Cmp 1",        minWidth: 230, flex: 1 },
    { field: "condicionActualComp1",    headerName: "Condición Actual Comp. 1",        minWidth: 210, flex: 1 },
    { field: "porcentajeCargaComp1",    headerName: "Porcentaje Carga Comp. 1 (%)",    minWidth: 250, flex: 1 },

    // Digitales
    { field: "controlCarga",            headerName: "Control de Carga",                 minWidth: 200, flex: 1 },
    { field: "ramploading",             headerName: "Ramp Loading",                     minWidth: 180, flex: 1 },
    { field: "switchFlujoAguaCondensacion", headerName: "Switch Flujo Agua Condensación", minWidth: 260, flex: 1 },
    { field: "switchFlujoAguaHelada",   headerName: "Switch Flujo Agua Helada",         minWidth: 230, flex: 1 },
    { field: "bajoNivelAceiteComp1",    headerName: "Bajo Nivel Aceite Comp. 1",        minWidth: 220, flex: 1 },
    { field: "statusTempMotorComp1",    headerName: "Status Temp Motor Comp. 1",        minWidth: 230, flex: 1 },
    { field: "interlockComp1",          headerName: "Interlock Comp. 1",                minWidth: 200, flex: 1 },
    { field: "statusMotorProtect1",     headerName: "Status MotorProtect 1",            minWidth: 210, flex: 1 },
    { field: "arranqueRemoto",          headerName: "Arranque Remoto",                  minWidth: 200, flex: 1 },
    { field: "paroEmergencia",          headerName: "Paro de Emergencia",               minWidth: 200, flex: 1 },
    { field: "encendidoComp1",          headerName: "Encendido Compresor 1",            minWidth: 210, flex: 1 },
    { field: "loadUnloadValvula1Comp1", headerName: "Load/Unload Válvula 1 Comp. 1",    minWidth: 250, flex: 1 },
    { field: "loadUnloadValvula2Comp1", headerName: "Load/Unload Válvula 2 Comp. 1",    minWidth: 250, flex: 1 },
    { field: "loadUnloadValvula3Comp1", headerName: "Load/Unload Válvula 3 Comp. 1",    minWidth: 250, flex: 1 },
    { field: "output04",                headerName: "Output 04",                         minWidth: 160, flex: 1 },
    { field: "output05",                headerName: "Output 05",                         minWidth: 160, flex: 1 },
    { field: "output06",                headerName: "Output 06",                         minWidth: 160, flex: 1 },
    { field: "output07",                headerName: "Output 07",                         minWidth: 160, flex: 1 },
    { field: "output08",                headerName: "Output 08",                         minWidth: 160, flex: 1 },
    { field: "output09",                headerName: "Output 09",                         minWidth: 160, flex: 1 },
    { field: "output10",                headerName: "Output 10",                         minWidth: 160, flex: 1 },
    { field: "encendidoCoThermC1",      headerName: "Encendido Co-Therm C1",            minWidth: 220, flex: 1 },
    { field: "output12",                headerName: "Output 12",                         minWidth: 160, flex: 1 },
    { field: "output13",                headerName: "Output 13",                         minWidth: 160, flex: 1 },
  ];


  const dummyData = [
  { fecha: "1", value: 2 },
  { fecha: "2", value: 5.5 },
  { fecha: "3", value: 2 },
  { fecha: "5", value: 8.5 },
  { fecha: "8", value: 1.5 },
  { fecha: "10", value: 5 },
  ];

  // 1) Si tengo datos reales los uso, si no pongo un dummy
  const displayData = rows.length ? rows : dummyData;

  // 2) De mis opciones, cojo sólo las que estén marcadas
  // 3) Si no hay ninguna marcada, dibujo la serie "value" del dummy
  const seriesKeys = React.useMemo(() => {
    const displaySeriesKeys = seriesOptions
      .filter(opt => selectedKeys.includes(opt.key))
      .map(opt => opt.key);
    return displaySeriesKeys.length
      ? displaySeriesKeys
      : (rows.length ? [] : ["value"]);
  }, [selectedKeys, rows.length]);

  // 4) Genero un color para cada serie
  const lineColors = React.useMemo(
    () => seriesKeys.map((_, i) => schemeCategory10[i % schemeCategory10.length]),
    [seriesKeys]
  );

  const labelMap = React.useMemo(() => {
  const m = Object.fromEntries(seriesOptions.map(o => [o.key, o.label]));
  m.value = "Valor (demo)";
  return m;
  }, []);


  // console.log("🔥 columns que llega a DataGrid:", columns);
    
     return (

       <Box sx={{ maxWidth: 1200, mx: "auto", mt: "90px", px: 2 }}>
      <Header
        title="Optichiller 1Comp CT6"
        sxTitle={{ fontSize: "2rem", textAlign: "center" }}
      />

      {/* Top Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="ID Equipo"
              variant="outlined"
              fullWidth
              value={idEquipo}
              onChange={(e) => setIdEquipo(e.target.value)}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="Fecha Inicio"
              variant="outlined"
              type="datetime-local"
              fullWidth
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <TextField
              label="Fecha Final"
              variant="outlined"
              type="datetime-local"
              fullWidth
              value={fechaFinal}
              onChange={(e) => setFechaFinal(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyles}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Middle Row: Variables con Tabs */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 2, height: 400, display: "flex", flexDirection: "column" }}>
            {/* Tabs para Análogos | Digitales */}
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ mb: 1 }}
            >
              <Tab label="Análogos" />
              <Tab label="Digitales" />
            </Tabs>

            {/* Panel Análogos */}
            {tabIndex === 0 && (
              <Box sx={{ flex: 1, overflowY: "auto", px: 1, '& .MuiFormControlLabel-root': { mb: 1 } }}>
                <Grid container spacing={1}>
                  {seriesOptions
                    .filter(opt => analogKeys.includes(opt.key))
                    .map(opt => (
                      <Grid item xs={6} key={opt.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedKeys.includes(opt.key)}
                              onChange={() => toggleKey(opt.key)}
                            />
                          }
                          label={<Typography variant="caption"  sx={{ fontSize: 12, fontWeight: 'bold' }}>{opt.label}</Typography>}
                        />
                      </Grid>
                    ))
                  }
                </Grid>
              </Box>
            )}

            {/* Panel Digitales */}
            {tabIndex === 1 && (
              <Box sx={{ flex: 1, overflowY: "auto", px: 1, '& .MuiFormControlLabel-root': { mb: 1 } }}>
                <Grid container spacing={1}>
                  {seriesOptions
                    .filter(opt => digitalKeys.includes(opt.key))
                    .map(opt => (
                      <Grid item xs={6} key={opt.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedKeys.includes(opt.key)}
                              onChange={() => toggleKey(opt.key)}
                            />
                          }
                          label={<Typography variant="caption" fontWeight="bold">{opt.label}</Typography>}
                        />
                      </Grid>
                    ))
                  }
                </Grid>
              </Box>
            )}

            {/* Botón Graficar */}
            <Button
              variant="contained"
              sx={{
                bgcolor: "#771726",
                color: "white",
                mt: 1,
                width: isMobile ? "100%" : "auto",
                "&:hover": { bgcolor: "#747274" },
              }}
              onClick={(e) => { e.preventDefault(); fetchRows(); }}
            >
              Graficar
            </Button>
          </Paper>
        </Grid>

        {/* Table placeholder */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={1}
            sx={{
              height: 400,       // la altura fija del Paper
              p: 2,
              display: "flex",   // IMPORTANTE → convierte al Paper en un contenedor flex
              flexDirection: "column"
            }}
          >

            {/*
              Aquí le cedemos todo el espacio restante al DataGrid
              envolviéndolo en un Box con flex: 1.
            */}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                disableColumnMenu
                pageSize={10}
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#771726",
                    zIndex: 1,
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: "#771726",
                    color: "#ffffff",
                    borderTop: "none",
                  },
                  "& .MuiTablePagination-root, & .MuiSvgIcon-root": {
                    color: "#ffffff",
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Final Row: Full‑width Chart */}
      <Grid container mb={3}>
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ height: 400, p: 2, pb: 4 }}>
            {loading ? (
              <Box sx={{display:"flex",justifyContent:"center",alignItems:"center",height:"100%"}}>
                <CircularProgress/>
              </Box>
            ) : (
              <>
                <Box
                  sx={{ 
                    display: "flex", 
                    overflowX: "auto", 
                    whiteSpace: "nowrap", 
                    mb: 1,                // espacio con el chart 
                    ml: "30px",           // desplazamiento igual al margin.left del chart 
                    width: "calc(100% - 60px)", // 100% menos left + right margins (20+30) 
                  }} 
                >
                  {seriesKeys.map((key, i) => (
                    <Box key={key} sx={{ display: "flex", alignItems: "center", mr: 3 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: lineColors[i],
                          mr: 1,
                        }}
                      />
                      <Typography variant="caption">{labelMap[key]}</Typography>
                    </Box>
                  ))}
                </Box>

                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    animationDuration={0}
                    data={displayData}
                    margin={{ top: 20, right: 30, left: -30, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="4 4" stroke={colors.grey[700]} />

                    <XAxis
                      dataKey="fecha"
                      interval="auto"
                      minTickGap={70}          
                      tickMargin={10}
                      tick={{ fill: colors.grey[300], fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: colors.grey[500] }}
                      tickFormatter={fmtNoSeconds} 
                    />
                    
                    <YAxis
                      tick={{ fill: colors.grey[300], fontSize: 12 }}
                      tickLine={false}
                      axisLine={{ stroke: colors.grey[500] }}
                      domain={["auto", "auto"]}
                    />

                    <Tooltip
                      content={<CustomTooltip />}
                      delayShow={0}
                      cursor={{ stroke: colors.grey[500], strokeDasharray: "3 3" }}
                    />

                    {/* Ya no va <Legend> aquí */}

                    {seriesKeys.map((key, i) => (
                      <Line
                        key={key}
                        dataKey={key}
                        name={labelMap[key]}
                        stroke={lineColors[i]}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 6 }}
                        isAnimationActive={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Error Snackbar */}
      <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GLicuador1Comp;